import React, { Fragment } from 'react'
import { PrismicRichText } from '@prismicio/react'
import { Popover, PopoverButton, PopoverPanel, Transition } from '@headlessui/react'
import { graphql, useStaticQuery } from 'gatsby'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import { useMergePrismicPreviewData } from 'gatsby-plugin-prismic-previews'

// Components
import Share from '../components/share'

const formatDate = (isoDateStr) => {
  const date = new Date(isoDateStr)
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0') // Months are 0-indexed
  const year = date.getFullYear()
  return `${day}/${month}/${year}`
}

const FoodMenu = ({ context, slice }) => {
  const staticData = useStaticQuery(graphql`
    query {
      prismicConfig {
        _previewable
        data {
          company_logo {
            alt
            gatsbyImageData(width: 240, placeholder: NONE)
            url
          }
        }
      }
    }
  `)

  const data = useMergePrismicPreviewData(staticData)
  const companyLogo = getImage(data.prismicConfig.data.company_logo.gatsbyImageData)
  const menu = slice.primary.menu.document
  const menuData = menu.data
  let columns = 'columns-1'
  if (menuData.number_of_columns === true) {
    columns = 'columns-1 md:columns-2 print:columns-2'
  }
  const menuSchema = {
    '@type': 'Menu',
    name: menuData.title.text,
    description: menuData.text.text,
    hasMenuSection: menuData.body.map((course) => ({
      '@type': 'MenuSection',
      name: course.primary.course_name,
      description: course.primary.course_description?.text,
      hasMenuItem: course.items.map((item) => ({
        '@type': 'MenuItem',
        name: item.item_name,
        description: `${item.item_description?.text || ''}${item.allergen ? ` Allergens: ${item.allergen}` : ''}`,
        offers: {
          '@type': 'Offer',
          price: item.item_price,
          priceCurrency: 'GBP'
        },
        suitableForDiet: [
          item.vegan ? 'http://schema.org/VeganDiet' : null,
          item.vegetarian ? 'http://schema.org/VegetarianDiet' : null,
          item.halal ? 'http://schema.org/HalalDiet' : null,
          item.gluten_free ? 'http://schema.org/GlutenFreeDiet' : null
        ].filter(Boolean)
      }))
    }))
  }

  return (
    <section id={menu.id} key={`menu-${menu.id}`} className='component-menu mx-auto max-w-screen-xl m-4 p-4 bg-white shadow-xl rounded-md print:overflow-hidden print:m-0 print:p-0 relative'>
      <div className='absolute inset-x-0 top-10 flex flex-row justify-center w-auto text-center md:right-10 md:justify-end print:hidden'>
        <Share title={menuData.title.text} link={context.url} image={data.prismicConfig.data.company_logo.url} hideText={true} printId={menu.id} />
      </div>
      <div className='border-double border-4 border-cyan-950/20 px-4 py-10 lg:p-10 print:p-4 print:w-[210mm] print:h-[297mm] print:flex print:flex-col'>
        <div className='hidden print:block print:mx-auto print:text-center print:mt-0'>
          <GatsbyImage className='invert w-32 print:w-20' loading='lazy' image={companyLogo} alt={data.prismicConfig.data.company_logo.alt || ''} />
        </div>
        <div className='prose lg:prose-xl max-w-none mb-8 print:prose-sm print:text-sm print:-mt-4 print:flex print:flex-col'>
          <div className='text-center'>
            <PrismicRichText field={menuData.title?.richText} />
          </div>
          <div className='font-semibold text-center mb-4 md:mb-10 print:-mt-8'>
            <PrismicRichText field={menuData.text?.richText} />
          </div>
          <div className={`${columns} gap-16`}>
            {menuData.body.map((course) => {
              return (
                <div className='mb-6' key={`course-${course.id}`}>
                  <div className='mb-2'>
                    {course.primary.course_name && <div className='text-2xl font-semibold font-serif border-b print:text-base'>{course.primary.course_name}</div>}
                    <PrismicRichText field={course.primary.course_description?.richText} />
                  </div>
                  <div>
                    {course.items.map((item, index) => {
                      return (
                        <div key={`item-${course.id}-${index}`} className={`break-inside-avoid-column ${item.item_description.text ? 'my-4' : ''}`}>
                          <div className='font-semibold flex justify-between print:text-sm'>
                            <div>
                              {item.item_name}

                              <div className='ml-1 font-italic text-sm inline-flex space-x-1 capitalize print:text-xs print:space-x-0'>
                                {item.vegetarian && (
                                  <abbr className='print:no-underline' title='Vegetarian'>
                                    (v)
                                  </abbr>
                                )}
                                {item.vegan && (
                                  <abbr className='print:no-underline' title='Vegan'>
                                    (ve)
                                  </abbr>
                                )}
                                {item.gluten_free && (
                                  <abbr className='print:no-underline' title='Gluten Free'>
                                    (gf)
                                  </abbr>
                                )}
                                {item.halal && (
                                  <abbr className='print:no-underline' title='Halal'>
                                    (h)
                                  </abbr>
                                )}
                                {item.allergens && (
                                  <Popover className='relative print:hidden'>
                                    {() => (
                                      <>
                                        <PopoverButton className='underline hover:no-underline'>
                                          <span>(Algy)</span>
                                        </PopoverButton>
                                        <Transition
                                          as={Fragment}
                                          enter='transition ease-out duration-200'
                                          enterFrom='opacity-0 translate-y-1'
                                          enterTo='opacity-100 translate-y-0'
                                          leave='transition ease-in duration-150'
                                          leaveFrom='opacity-100 translate-y-0'
                                          leaveTo='opacity-0 translate-y-1'>
                                          <PopoverPanel className='absolute -top-12 left-1/2 z-10 -translate-x-1/2 w-auto whitespace-nowrap text-center transform'>
                                            <div className='overflow-hidden text-sm text-white rounded-sm shadow-lg p-2 bg-black'>
                                              <span>{item.allergens}</span>
                                            </div>
                                          </PopoverPanel>
                                        </Transition>
                                      </>
                                    )}
                                  </Popover>
                                )}
                              </div>
                            </div>
                            {item.item_price && <div>£{item.item_price.toFixed(2)}</div>}
                          </div>
                          <div className='not-prose text-base print:text-xs'>
                            <PrismicRichText field={item.item_description?.richText} />
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
        <div className='mt-8 print:text-[10px] print:leading-snug print:mt-auto prose prose-sm w-full max-w-none'>
          <p>
            Do you have any allergies? Before placing your order for food or drink please notify our staff members who will help you. We have details of our food and drink menu and the 14 legally
            declarable allergens which may be present in any dish. Whilst all reasonable steps have been taken to avoid the unintentional presence of allergens, we cannot guarantee that any product is
            100% free of allergens.
          </p>
          <div className='flex justify-between'>
            <div>Prices subject to change. </div>
            <div>Last updated {formatDate(menu.last_publication_date)}</div>
          </div>
        </div>

        <script type='application/ld+json'>{JSON.stringify(menuSchema)}</script>
      </div>
    </section>
  )
}

export default FoodMenu
